import { CommonUIError } from '@afterpay/types'
import React, { forwardRef, useEffect, useState } from 'react'

import { Locale } from '../i18n'
import { Input, InputOnChange, InputOnFocus, InputSizes } from '../Input'
import { DesignLanguage, useIsDesignLanguage } from '../Theme'
import { noop } from '../utils/function'
import { InputBirthdayDateFormatPosition, InputBirthdayValidity, useDob } from './helper'
export interface InputBirthdayProps extends CommonUIError {
  defaultValue?: string
  infoText?: string
  id?: string
  hasDateFormatLabel?: boolean
  dateFormatPosition?: InputBirthdayDateFormatPosition
  readOnly?: boolean
  label?: string
  locale?: Locale
  maxAge?: number
  minAge?: number
  name?: string
  onBlur?: (e: InputOnFocus, dob: string, validity: InputBirthdayValidity) => void
  onChange?: (e: InputOnChange, dob: string, validity: InputBirthdayValidity) => void
  onValidationChange?: (dob: string, validity: InputBirthdayValidity, revalidate: boolean, name: string) => void
  size?: InputSizes
  testNameSpace?: string
  fullWidth?: boolean
}

export const InputBirthday = forwardRef<HTMLInputElement, InputBirthdayProps>(
  (
    {
      defaultValue = '', // 1999-12-31
      error = false,
      errorText = '',
      label = '',
      locale = 'en-US',
      maxAge = 110,
      minAge = 18,
      name = '',
      hasDateFormatLabel = true,
      dateFormatPosition = InputBirthdayDateFormatPosition.PrimaryLabel,
      onBlur = noop,
      onChange = noop,
      onValidationChange = noop,
      size = 'Large',
      testNameSpace = '',
      fullWidth = false,
      ...otherProps
    },
    ref
  ) => {
    const isArcadeDesign = useIsDesignLanguage(DesignLanguage.Arcade)
    const [currentInputBirthdayValue, setCurrentInputBirthdayValue] = useState<string>('')
    const { parseISODateString, parseFormattedDateString, dateFormat } = useDob({ minAge, maxAge, locale })
    const { validity, formattedDate } = parseISODateString(defaultValue)
    const primaryLabel = `${label}${hasDateFormatLabel && dateFormatPosition === InputBirthdayDateFormatPosition.PrimaryLabel ? ` ${dateFormat}` : ''}`
    const secondaryLabel = !isArcadeDesign && hasDateFormatLabel && dateFormatPosition === InputBirthdayDateFormatPosition.SecondaryLabel ? dateFormat : ''

    const handleChange = (e: InputOnChange): void => {
      const { validity, isoDate } = parseFormattedDateString(e.target.value)
      setCurrentInputBirthdayValue(e.target.value)
      onChange(e, isoDate, validity)
    }

    const handleBlur = (e: InputOnFocus): void => {
      const { validity, isoDate } = parseFormattedDateString(e.target.value)
      setCurrentInputBirthdayValue(e.target.value)
      onBlur(e, isoDate, validity)
    }

    useEffect(() => {
      if (currentInputBirthdayValue) {
        const { validity, isoDate } = parseFormattedDateString(currentInputBirthdayValue)
        onValidationChange(isoDate, validity, true, name)
      }
    }, [minAge, maxAge])

    return (
      <Input.Tel
        label={primaryLabel}
        secondaryLabel={secondaryLabel}
        value={validity === InputBirthdayValidity.Valid ? formattedDate : ''}
        onChange={handleChange}
        onBlur={handleBlur}
        format={dateFormat.replace(/D|M|Y/gi, '0')}
        error={error}
        errorText={errorText}
        name={name}
        autoComplete='bday'
        size={size}
        ref={ref}
        fullWidth={fullWidth}
        placeholder={isArcadeDesign ? dateFormat : undefined}
        {...(testNameSpace.length > 0 && { testNameSpace })}
        {...otherProps}
      />
    )
  }
)
